// ============================================================================
// Files (docs/areas/files.md): the owner's own Dropbox. The tree is the File
// records on the device (synced, so it reads offline); the bytes are in the
// bucket and come through presigned URLs. /files is the top of the current
// suite (tabs: Files, Recent, Starred, Deleted; search by name), /files/$id a
// folder or a file. Phone first: one column of rows or a grid of thumbnails,
// Upload in the title, the upload tray above the tab bar; drag and drop on a
// desk.
// ============================================================================

import { useMemo, useRef, useState, type DragEvent } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  ChevronRight,
  CloudOff,
  Download,
  FolderPlus,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Search,
  Share2,
  Star,
  Upload,
} from 'lucide-react'
import { mutate } from '@/runtime'
import { useFilesWithTrash } from '@/db/hooks'
import type { FileResource } from '@/kinds/file/apply'
import { fileUrl, filesStatus, lastFilesStatus } from '@/lib/files/api'
import {
  childrenOf,
  deletedWith,
  freeName,
  pathTo,
  recentFiles,
  searchFiles,
  sortFiles,
  starredFiles,
  trashOf,
  type Sort,
  type SortKey,
} from '@/lib/files/tree'
import { formatSize, typeLabel } from '@/lib/files/types'
import { resumableHere, resumeUpload, uploadFiles, useUploads } from '@/lib/files/uploads'
import { AppTabs } from '@/components/AppTabs'
import { Phrases } from '@/components/Phrases'
import { Button, Empty, IconButton, PageTitle } from '@/components/ui'
import { useScope } from '@/state/scope'
import { useToasts } from '@/state/toasts'
import { ALL } from '@/kinds/workspace/schema'
import { ActionsSheet, NameSheet, restoreItem } from './sheets'
import { ShareFileSheet } from './ShareFileSheet'
import { FileViewer } from './Viewer'
import { Thumb, UploadTray, downloadFile, useThumbs } from './parts'
import { useAsync } from './useAsync'

type Tab = 'files' | 'recent' | 'starred' | 'deleted'
const TABS: { tab: Tab; label: string }[] = [
  { tab: 'files', label: 'Files' },
  { tab: 'recent', label: 'Recent' },
  { tab: 'starred', label: 'Starred' },
  { tab: 'deleted', label: 'Deleted' },
]

// per device: how the owner likes to look at a folder
const LOOK = 'files-look'
const readLook = (): { grid: boolean; sort: Sort } => {
  try {
    return {
      grid: false,
      sort: { key: 'name' },
      ...(JSON.parse(localStorage.getItem(LOOK) ?? '{}') as object),
    }
  } catch {
    return { grid: false, sort: { key: 'name' } }
  }
}
const saveLook = (l: { grid: boolean; sort: Sort }) => {
  try {
    localStorage.setItem(LOOK, JSON.stringify(l))
  } catch {
    /* private mode */
  }
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })

/** Whether Files has a bucket: the server's answer, the last one kept for offline. */
const useFilesOn = (): boolean | undefined => {
  const st = useAsync('files-status', filesStatus)
  return st.data?.enabled ?? lastFilesStatus()?.enabled
}

const FilesOff = () => (
  <Empty icon={<CloudOff size={30} strokeWidth={1.5} aria-hidden />}>
    Files has nowhere to keep things yet. It needs an S3 bucket: set ALFIEOS_FILES_S3_ENDPOINT, _BUCKET,
    _ACCESS_KEY_ID and _SECRET_ACCESS_KEY on the server (docs/areas/files.md), then come back.
  </Empty>
)

/** /files: the top of the current suite, or one of the tabs. */
export const FilesView = () => {
  const search = useSearch({ strict: false }) as { tab?: Tab; q?: string }
  return <Browser folderId={null} tab={search.tab ?? 'files'} q={search.q ?? ''} />
}

/** /files/$id: a folder (the browser) or a file (its page). */
export const FileRouteView = () => {
  const { id } = useParams({ strict: false }) as { id: string }
  const all = useFilesWithTrash()
  const item = all?.find((f) => f.id === id)
  if (all === undefined) return null
  if (!item || item.deleted_at)
    return (
      <div data-testid="files-gone">
        <PageTitle>This {item?.spec.folder ? 'folder' : 'file'} is gone</PageTitle>
        <p className="text-[15px] text-fg-2 mb-4">
          {item?.deleted_at
            ? 'It was deleted. Files keeps it in Deleted for 30 days.'
            : 'It is not on this device.'}
        </p>
        <Link to="/files" search={{ tab: item?.deleted_at ? 'deleted' : undefined }} className="text-accent">
          {item?.deleted_at ? 'Open Deleted' : 'Back to Files'}
        </Link>
      </div>
    )
  if (item.spec.folder) return <Browser folderId={item.id} tab="files" q="" />
  return <FilePage file={item} all={all} />
}

const Crumbs = ({ path }: { path: FileResource[] }) => (
  <nav
    aria-label="Folders"
    className="flex items-center flex-wrap gap-0.5 text-[13px] text-muted mb-1 -mt-1"
    data-testid="files-crumbs"
  >
    <Link to="/files" className="hover:text-fg min-h-8 inline-flex items-center">
      Files
    </Link>
    {path.map((f) => (
      <span key={f.id} className="inline-flex items-center gap-0.5 min-w-0">
        <ChevronRight size={14} aria-hidden className="text-faint" />
        <Link
          to="/files/$id"
          params={{ id: f.id }}
          className="hover:text-fg truncate max-w-[12rem] min-h-8 inline-flex items-center"
        >
          {f.spec.name}
        </Link>
      </span>
    ))}
  </nav>
)

const Browser = ({ folderId, tab, q }: { folderId: string | null; tab: Tab; q: string }) => {
  const navigate = useNavigate()
  const on = useFilesOn()
  const ws = useScope((s) => s.ws)
  const all = useFilesWithTrash()
  const [look, setLook] = useState(readLook)
  const [query, setQuery] = useState(q)
  const [sheet, setSheet] = useState<
    { kind: 'new' } | { kind: 'actions'; item: FileResource } | { kind: 'share'; item: FileResource } | null
  >(null)
  const [dragging, setDragging] = useState(false)
  const picker = useRef<HTMLInputElement>(null)
  const files = useMemo(() => all ?? [], [all])
  const live = useMemo(() => files.filter((f) => !f.deleted_at), [files])
  const folder = folderId ? live.find((f) => f.id === folderId) : undefined
  const path = folderId ? pathTo(live, folderId) : []
  const searching = query.trim().length > 0
  const shown = useMemo(() => {
    if (searching) return searchFiles(live, query)
    if (tab === 'recent') return recentFiles(live)
    if (tab === 'starred') return starredFiles(live)
    if (tab === 'deleted') return trashOf(files)
    return sortFiles(childrenOf(live, folderId), look.sort)
  }, [searching, query, tab, live, files, folderId, look.sort])
  const thumbs = useThumbs(tab === 'deleted' ? [] : shown)
  const setSort = (key: SortKey) => {
    const next = { ...look, sort: { key, desc: look.sort.key === key ? !look.sort.desc : key !== 'name' } }
    setLook(next)
    saveLook(next)
  }
  const toggleGrid = () => {
    const next = { ...look, grid: !look.grid }
    setLook(next)
    saveLook(next)
  }
  const upload = (list: FileList | File[] | null) => {
    const picked = list ? Array.from(list) : []
    if (!picked.length) return
    if (on === false) return useToasts.getState().show('Files has no bucket yet')
    // into the folder's own suite, whatever the scope says
    void uploadFiles(picked, folderId, folder?.labels.ws ?? (ws !== ALL ? ws : undefined))
  }
  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragging(false)
    if (tab === 'files' && !searching) upload(e.dataTransfer.files)
  }
  const open = (f: FileResource) =>
    f.deleted_at ? undefined : void navigate({ to: '/files/$id', params: { id: f.id } })
  if (on === false) {
    return (
      <div data-testid="files">
        <PageTitle>Files</PageTitle>
        <FilesOff />
      </div>
    )
  }
  const title = folder ? folder.spec.name : 'Files'
  return (
    <div
      data-testid="files"
      className="relative min-h-[60vh]"
      onDragOver={(e) => {
        if (tab !== 'files' || searching) return
        e.preventDefault()
        setDragging(true)
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) setDragging(false)
      }}
      onDrop={onDrop}
    >
      {folder && <Crumbs path={path.slice(0, -1)} />}
      <PageTitle
        right={
          tab === 'files' && !searching ? (
            <div className="flex items-center gap-1">
              {folder && (
                <IconButton
                  label={`Share ${folder.spec.name}`}
                  onClick={() => setSheet({ kind: 'share', item: folder })}
                  data-testid="files-folder-share"
                >
                  <Share2 size={20} />
                </IconButton>
              )}
              <IconButton
                label="New folder"
                onClick={() => setSheet({ kind: 'new' })}
                data-testid="files-new-folder"
              >
                <FolderPlus size={20} />
              </IconButton>
              <Button variant="primary" onClick={() => picker.current?.click()} data-testid="files-upload">
                <Upload size={16} aria-hidden /> Upload
              </Button>
            </div>
          ) : undefined
        }
      >
        <span className="break-words">{title}</span>
      </PageTitle>
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          upload(e.target.files)
          e.target.value = ''
        }}
        data-testid="files-picker"
      />
      {!folder && (
        <AppTabs
          label="Files"
          tabs={TABS.map((t) => ({
            to: '/files',
            label: t.label,
            search: (t.tab === 'files' ? {} : { tab: t.tab }) as Record<string, string>,
            on: tab === t.tab && !searching,
          }))}
        />
      )}
      <div className="flex items-center gap-2 mb-3">
        <label className="flex-1 min-w-0 flex items-center gap-2 rounded-full bg-raised border border-line px-3 min-h-10">
          <Search size={16} className="text-faint shrink-0" aria-hidden />
          <input
            className="bare-input flex-1 min-w-0 bg-transparent text-[15px]"
            placeholder="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search files by name"
            data-testid="files-search"
          />
        </label>
        {tab === 'files' && !searching && (
          <select
            aria-label="Sort"
            className="rounded-full bg-raised border border-line px-3 min-h-10 text-[13px]"
            value={look.sort.key}
            onChange={(e) => setSort(e.target.value as SortKey)}
            data-testid="files-sort"
          >
            <option value="name">Name</option>
            <option value="modified">Date</option>
            <option value="size">Size</option>
            <option value="kind">Type</option>
          </select>
        )}
        <IconButton
          label={look.grid ? 'Show as a list' : 'Show as a grid'}
          onClick={toggleGrid}
          data-testid="files-grid-toggle"
        >
          {look.grid ? <ListIcon size={20} /> : <LayoutGrid size={20} />}
        </IconButton>
      </div>
      {all === undefined ? null : shown.length === 0 ? (
        <Empty
          icon={<Upload size={30} strokeWidth={1.5} aria-hidden />}
          action={
            tab === 'files' && !searching ? (
              <Button variant="primary" onClick={() => picker.current?.click()}>
                Upload files
              </Button>
            ) : undefined
          }
        >
          {searching
            ? `Nothing here is called “${query.trim()}”.`
            : tab === 'recent'
              ? 'Nothing uploaded yet.'
              : tab === 'starred'
                ? 'Star a file or folder to keep it here.'
                : tab === 'deleted'
                  ? 'Nothing deleted. Deleted files stay here for 30 days, then their bytes go for good.'
                  : folder
                    ? 'This folder is empty. Drop files here or upload them.'
                    : 'Keep anything here: pictures, videos, documents, code. Drop files here or upload them.'}
        </Empty>
      ) : tab === 'deleted' ? (
        <TrashList items={shown} all={files} />
      ) : look.grid ? (
        <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="files-grid">
          {shown.map((f) => (
            <li key={f.id} className="relative">
              <button
                type="button"
                onClick={() => open(f)}
                onContextMenu={(e) => {
                  e.preventDefault()
                  setSheet({ kind: 'actions', item: f })
                }}
                className="w-full text-left card overflow-hidden"
                data-testid="files-tile"
              >
                <Thumb file={f} url={thumbs[f.id]} className="w-full aspect-square" icon={34} />
                <span className="flex items-center gap-1 px-2.5 py-2">
                  <span className="text-[13px] truncate flex-1">{f.spec.name}</span>
                  {f.spec.starred && (
                    <Star
                      size={12}
                      className="text-faint shrink-0"
                      fill="currentColor"
                      aria-label="Starred"
                    />
                  )}
                </span>
              </button>
              {/* touch screens have no right-click, so the tile carries its own More */}
              <IconButton
                label={`More for ${f.spec.name}`}
                onClick={() => setSheet({ kind: 'actions', item: f })}
                className="absolute top-1 right-1 bg-raised/80 backdrop-blur-sm"
                data-testid="files-tile-more"
              >
                <MoreHorizontal size={18} />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="group-list" data-testid="files-list">
          {shown.map((f) => (
            <Row
              key={f.id}
              f={f}
              thumb={thumbs[f.id]}
              where={searching || tab !== 'files' ? pathTo(live, f.spec.parent ?? null) : undefined}
              onOpen={() => open(f)}
              onMore={() => setSheet({ kind: 'actions', item: f })}
            />
          ))}
        </ul>
      )}
      {dragging && (
        <div
          className="absolute inset-0 rounded-[var(--radius-card)] border-2 border-dashed border-accent bg-accent/10 flex items-center justify-center pointer-events-none"
          aria-hidden
        >
          <span className="text-[15px] font-medium">Drop to upload into {title}</span>
        </div>
      )}
      {sheet?.kind === 'new' && (
        <NameSheet
          title="New folder"
          initial={freeName(childrenOf(live, folderId), 'New folder')}
          siblings={childrenOf(live, folderId)}
          action="Make it"
          onSave={async (name) => {
            await mutate.create(
              'File',
              { name, folder: true, ...(folderId ? { parent: folderId } : {}) },
              folder ? { ws: folder.labels.ws! } : {},
            )
          }}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === 'actions' && (
        <ActionsSheet
          item={sheet.item}
          all={files}
          onShare={() => setSheet({ kind: 'share', item: sheet.item })}
          onClose={() => setSheet(null)}
        />
      )}
      {sheet?.kind === 'share' && <ShareFileSheet item={sheet.item} onClose={() => setSheet(null)} />}
      <UploadTray />
    </div>
  )
}

const Row = ({
  f,
  thumb,
  where,
  onOpen,
  onMore,
}: {
  f: FileResource
  thumb?: string
  /** the folder it is in, when the list is not one folder (search, Recent, Starred) */
  where?: FileResource[]
  onOpen: () => void
  onMore: () => void
}) => {
  const uploading = !f.spec.folder && f.status.state !== 'ready'
  return (
    <li className="flex items-center" data-testid="files-row">
      <button
        type="button"
        onClick={onOpen}
        className="flex-1 min-w-0 flex items-center gap-3 pl-3 pr-1 py-2 text-left"
      >
        <Thumb file={f} url={thumb} className="h-10 w-10 shrink-0 rounded-[var(--radius-card-inner)]" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <span className="text-[15px] truncate">{f.spec.name}</span>
            {f.spec.starred && (
              <Star size={12} className="text-faint shrink-0" fill="currentColor" aria-label="Starred" />
            )}
          </span>
          <Phrases
            className="text-xs text-faint"
            parts={[
              uploading ? 'Uploading' : !f.spec.folder && formatSize(f.spec.size),
              fmtDate(f.status.uploaded_at ?? f.updated_at),
              where && (where.length ? `in ${where.map((p) => p.spec.name).join(' / ')}` : 'in Files'),
            ]}
          />
        </span>
      </button>
      <IconButton
        label={`More for ${f.spec.name}`}
        onClick={onMore}
        className="mr-1"
        data-testid="files-more"
      >
        <MoreHorizontal size={18} />
      </IconButton>
    </li>
  )
}

const TrashList = ({ items, all }: { items: FileResource[]; all: readonly FileResource[] }) => (
  <ul className="group-list" data-testid="files-trash">
    {items.map((f) => {
      const withIt = deletedWith(all, f)
      return (
        <li key={f.id} className="px-3.5 py-2.5 flex items-center gap-3">
          <Thumb file={f} className="h-10 w-10 shrink-0 rounded-[var(--radius-card-inner)]" />
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] truncate">{f.spec.name}</span>
            <Phrases
              className="text-xs text-faint"
              parts={[
                f.spec.folder
                  ? withIt.length
                    ? `with ${withIt.length} inside`
                    : 'Folder'
                  : formatSize(f.spec.size),
                f.deleted_at && `deleted ${fmtDate(f.deleted_at)}`,
              ]}
            />
          </span>
          <Button variant="chip" onClick={() => void restoreItem(f, withIt)} data-testid="files-restore">
            Restore
          </Button>
        </li>
      )
    })}
  </ul>
)

/** A file's own page: what it is, the viewer, and what can be done with it. */
const FilePage = ({ file, all }: { file: FileResource; all: readonly FileResource[] }) => {
  const navigate = useNavigate()
  const [sheet, setSheet] = useState<'actions' | 'share' | null>(null)
  const live = all.filter((f) => !f.deleted_at)
  const path = pathTo(live, file.spec.parent ?? null)
  const ready = file.status.state === 'ready'
  const getUrl = (variant: 'view' | 'download') => fileUrl(file.id, variant)
  return (
    <div data-testid="file-page">
      <Crumbs path={path} />
      <h1 className="t-title2 break-all mb-1">{file.spec.name}</h1>
      <Phrases
        className="text-[13px] text-muted mb-3"
        parts={[
          typeLabel(file.spec.name, file.spec.content_type),
          formatSize(file.spec.size),
          ready && file.status.uploaded_at && fmtDate(file.status.uploaded_at),
        ]}
      />
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {ready && (
          <Button variant="primary" onClick={() => void downloadFile(file.id)} data-testid="file-download">
            <Download size={16} aria-hidden /> Download
          </Button>
        )}
        {ready && (
          <Button onClick={() => setSheet('share')} data-testid="file-share">
            <Share2 size={16} aria-hidden /> Share
          </Button>
        )}
        <IconButton
          label={file.spec.starred ? 'Unstar' : 'Star'}
          onClick={() => void mutate.patch(file.id, { spec: { starred: file.spec.starred ? null : true } })}
        >
          <Star size={20} fill={file.spec.starred ? 'currentColor' : 'none'} />
        </IconButton>
        <IconButton label="More" onClick={() => setSheet('actions')} data-testid="file-more">
          <MoreHorizontal size={20} />
        </IconButton>
      </div>
      {ready ? (
        <FileViewer
          file={{
            id: file.id,
            name: file.spec.name,
            size: file.spec.size,
            contentType: file.spec.content_type,
          }}
          getUrl={getUrl}
          onDownload={() => void downloadFile(file.id)}
        />
      ) : (
        <Unfinished file={file} />
      )}
      {sheet === 'actions' && (
        <ActionsSheet
          item={file}
          all={all}
          onShare={() => setSheet('share')}
          onClose={() => setSheet(null)}
          onDeleted={() =>
            void navigate(
              file.spec.parent ? { to: '/files/$id', params: { id: file.spec.parent } } : { to: '/files' },
            )
          }
        />
      )}
      {sheet === 'share' && <ShareFileSheet item={file} onClose={() => setSheet(null)} />}
      <UploadTray />
    </div>
  )
}

/** A file whose bytes are on their way, or never arrived. */
const Unfinished = ({ file }: { file: FileResource }) => {
  const up = useUploads((s) => s.uploads.find((u) => u.id === file.id))
  const picker = useRef<HTMLInputElement>(null)
  const sending = up && (up.state === 'hashing' || up.state === 'sending')
  const canResume = !sending && !!resumableHere(file.id)
  return (
    <div className="card px-4 py-6 text-center" data-testid="file-unfinished">
      <p className="text-[15px] mb-1">{sending ? 'Uploading…' : 'This upload did not finish.'}</p>
      <p className="text-sm text-muted mb-3">
        {sending
          ? `${Math.round(((up.sent || 0) / (up.size || 1)) * 100)}% sent`
          : canResume
            ? 'Pick the same file again to send the rest.'
            : 'Upload it again; this copy is tidied away after a day.'}
      </p>
      {canResume && (
        <>
          <Button variant="primary" onClick={() => picker.current?.click()}>
            Resume
          </Button>
          <input
            ref={picker}
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (f) void resumeUpload(file, f).catch((err: Error) => useToasts.getState().show(err.message))
            }}
          />
        </>
      )}
    </div>
  )
}
